/* eslint-disable react-hooks/exhaustive-deps */
import { LastThingEvent } from '@eagle/core-data-types';
import { EthingsRestClient } from '@eagle/ethings-rest-client';
import { LRUCache } from 'lru-cache';
import { Duration } from 'luxon';
import { createContext, FC, PropsWithChildren, useContext, useEffect, useMemo, useRef } from 'react';
import { useAuthenticated } from '../auth';
import { LAST_CONTACT_EXCLUDED_EVENTS } from '../constants';
import { Undefinable } from '../types';

interface Props {
  cacheTtl?: Duration;
}

export interface LastThingEventsByFeature { [feature: string]: { [eventTypeId: string]: LastThingEvent } }

export interface LastThingEvents {
  byFeature: LastThingEventsByFeature;
  latest: Undefinable<LastThingEvent>;
  events: LastThingEvent[];
}

type ContextProps = [
  getLastEvents: (thingId: string) => Promise<LastThingEvents | undefined>,
  invalidateLastEvents: (thingId: string) => void,
];

type Fetcher<K, V> = (key: K, signal: AbortSignal) => Promise<V | undefined | void> | V | undefined | void;

const ERROR_NO_PROVIDER = (): never => { throw new Error('useLastThingState must be inside a LastThingStateProvider'); };

export const LastThingStateContext = createContext<ContextProps>([ERROR_NO_PROVIDER, ERROR_NO_PROVIDER]);

const lastThingEventFetcher = (restClient: EthingsRestClient) => {
  return (thingId: string, signal: AbortSignal) => restClient.lastThingEvent.getWithThing(thingId, { sort: '-occurred', filter: { eventTypeId: { '$nin': LAST_CONTACT_EXCLUDED_EVENTS } } }, { signal, chunkSize: 500 })
    .then((result) => {
      return {
        byFeature: result.items.reduce<LastThingEventsByFeature>((acc, evt) => ({ ...acc, [evt.feature]: { ...acc[evt.feature], [evt.eventTypeId]: evt } }), {}),
        latest: result.items[0],
        events: result.items,
      };
    });
};

export const LastThingStateProvider: FC<PropsWithChildren<Props>> = ({ cacheTtl = Duration.fromObject({ minute: 1 }), children }) => {
  const auth = useAuthenticated();
  const fetcher = useRef<Fetcher<string, LastThingEvents>>(lastThingEventFetcher(auth.restClient));
  useEffect(() => {
    fetcher.current = lastThingEventFetcher(auth.restClient);
  }, [auth.restClient]);

  const value = useMemo<ContextProps>(() => {
    const cache = new LRUCache<string, LastThingEvents>({
      max: 10000,
      ttl: cacheTtl.toMillis(),
      fetchMethod: (thingId, _, { signal }) => {
        return fetcher.current(thingId, signal);
      },
    });
    return [
      (thingId) => cache.fetch(thingId),
      (thingId) => cache.delete(thingId),
    ];
  }, [auth.user, auth.switchedTokenProvider]);

  return (
    <LastThingStateContext.Provider value={value}>
      {children}
    </LastThingStateContext.Provider>
  );
};

export const useLastThingState = (): ContextProps => useContext(LastThingStateContext);

